import React from 'react';
import { PRStatus, PRItemStatus } from '@prisma/client';

export const PASTEL_THEME = {
  primary: '#6366F1', // Lavender / Indigo
  primaryHover: '#4F46E5',
  primaryActive: '#4338CA',
  primaryLight: '#EEF2FF',
  background: '#F8FAFC',
  cardBg: '#FFFFFF',
  borderRadius: 14,
  borderSubtle: '#F1F5F9',
  boxShadow: '0 4px 20px -2px rgba(99, 102, 241, 0.06), 0 2px 6px -1px rgba(0, 0, 0, 0.04)',
  boxShadowCard: '0 1px 3px rgba(0,0,0,0.05), 0 10px 25px -5px rgba(99, 102, 241, 0.05)',
  boxShadowHover: '0 12px 30px -4px rgba(99, 102, 241, 0.12), 0 4px 10px -2px rgba(0, 0, 0, 0.04)',
};

/**
 * Cheerful soft pastel badge styling as defined in Firstmate spec:
 * - DRAFT: soft slate pastel (#F1F5F9 text #475569, border #CBD5E1)
 * - SUBMITTED / PENDING / WAITING: soft sky/cyan pastel (#E0F2FE text #0369A1, border #BAE6FD)
 * - APPROVED: soft mint pastel (#DCFCE7 text #15803D, border #BBF7D0)
 * - PARTIALLY_APPROVED: soft amber/apricot pastel (#FEF3C7 text #B45309, border #FDE68A)
 * - REJECTED: soft rose pastel (#FFE4E6 text #BE123C, border #FECDD3)
 */
export const PASTEL_BADGES = {
  DRAFT: {
    bg: '#F1F5F9',
    text: '#475569',
    border: '#CBD5E1',
    label: 'Draft',
    dotColor: '#94A3B8',
  },
  SUBMITTED: {
    bg: '#E0F2FE',
    text: '#0369A1',
    border: '#BAE6FD',
    label: 'Submitted',
    dotColor: '#38BDF8',
  },
  WAITING_APPROVAL: {
    bg: '#E0F2FE',
    text: '#0369A1',
    border: '#BAE6FD',
    label: 'Waiting Approval',
    dotColor: '#0284C7',
  },
  APPROVED: {
    bg: '#DCFCE7',
    text: '#15803D',
    border: '#BBF7D0',
    label: 'Approved',
    dotColor: '#22C55E',
  },
  PARTIALLY_APPROVED: {
    bg: '#FEF3C7',
    text: '#B45309',
    border: '#FDE68A',
    label: 'Partially Approved',
    dotColor: '#F59E0B',
  },
  REJECTED: {
    bg: '#FFE4E6',
    text: '#BE123C',
    border: '#FECDD3',
    label: 'Rejected',
    dotColor: '#F43F5E',
  },
  PENDING: {
    bg: '#E0F2FE',
    text: '#0369A1',
    border: '#BAE6FD',
    label: 'Pending',
    dotColor: '#0284C7',
  },
  // PO & GR Status Badges
  ISSUED: {
    bg: '#E0F2FE',
    text: '#0369A1',
    border: '#BAE6FD',
    label: 'Issued',
    dotColor: '#0284C7',
  },
  WAITING_DELIVERY: {
    bg: '#EEF2FF',
    text: '#4338CA',
    border: '#C7D2FE',
    label: 'Menunggu Pengiriman',
    dotColor: '#6366F1',
  },
  PARTIALLY_RECEIVED: {
    bg: '#FEF3C7',
    text: '#B45309',
    border: '#FDE68A',
    label: 'Diterima Sebagian',
    dotColor: '#F59E0B',
  },
  FULLY_RECEIVED: {
    bg: '#DCFCE7',
    text: '#15803D',
    border: '#BBF7D0',
    label: 'Selesai',
    dotColor: '#22C55E',
  },
  COMPLETED: {
    bg: '#DCFCE7',
    text: '#15803D',
    border: '#BBF7D0',
    label: 'Selesai',
    dotColor: '#16A34A',
  },
  CONFIRMED: {
    bg: '#DCFCE7',
    text: '#15803D',
    border: '#BBF7D0',
    label: 'Selesai',
    dotColor: '#16A34A',
  },
  CANCELLED: {
    bg: '#FFE4E6',
    text: '#BE123C',
    border: '#FECDD3',
    label: 'Dibatalkan',
    dotColor: '#F43F5E',
  },
};

export function PastelStatusBadge({
  status,
  size = 'medium',
}: {
  status: PRStatus | PRItemStatus | string;
  size?: 'small' | 'medium' | 'large';
}) {
  const badgeStyle = PASTEL_BADGES[status as keyof typeof PASTEL_BADGES] || PASTEL_BADGES.DRAFT;

  const fontSizes = {
    small: 11,
    medium: 12,
    large: 13,
  };

  const paddings = {
    small: '2px 8px',
    medium: '4px 12px',
    large: '6px 16px',
  };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: paddings[size],
        backgroundColor: badgeStyle.bg,
        color: badgeStyle.text,
        border: `1px solid ${badgeStyle.border}`,
        borderRadius: 20,
        fontWeight: 600,
        fontSize: fontSizes[size],
        lineHeight: 1.4,
        letterSpacing: '0.02em',
        boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
      }}
    >
      <span
        style={{
          width: size === 'small' ? 5 : 7,
          height: size === 'small' ? 5 : 7,
          borderRadius: '50%',
          backgroundColor: badgeStyle.dotColor,
        }}
      />
      {badgeStyle.label}
    </span>
  );
}

export function GoodQtyBadge({ qty, uom }: { qty: number | string; uom?: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        backgroundColor: '#DCFCE7',
        color: '#15803D',
        border: '1px solid #BBF7D0',
        borderRadius: 20,
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1.3,
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700 }}>✓</span>
      {qty} {uom || ''}
    </span>
  );
}

export function RejectedQtyBadge({ qty, uom }: { qty: number | string; uom?: string }) {
  const isZero = Number(qty) === 0;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 10px',
        backgroundColor: isZero ? '#F8FAFC' : '#FFE4E6',
        color: isZero ? '#94A3B8' : '#BE123C',
        border: `1px solid ${isZero ? '#E2E8F0' : '#FECDD3'}`,
        borderRadius: 20,
        fontWeight: 600,
        fontSize: 12,
        lineHeight: 1.3,
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700 }}>{isZero ? '-' : '✕'}</span>
      {qty} {uom || ''}
    </span>
  );
}

